import React, { useEffect, useState, useCallback } from "react";
import { Table, Card, Row, Col, Badge, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { useLocation } from "react-router-dom";

const TransactionSummary = () => {
  const location = useLocation();
  const { reservation, API_BASE, token, branch } = location.state || {};
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    //"/:branch/bill/:bookingId/summary",
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}transactions/summary/${branch}/${reservation._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setSummary(res.data.data);
      }
    } catch (err) {
      console.error("Fetch Summary Error:", err);
      setError("Failed to load transaction summary.");
    } finally {
      setLoading(false);
    }
  }, [branch, reservation._id, API_BASE, token]);

  useEffect(() => {
    if (reservation?._id) fetchSummary();
  }, [fetchSummary, reservation?._id]);

  if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!summary) return <Alert variant="info">No transactions found for this bill.</Alert>;

  return (
    <div className="transaction-summary">
      {/* 1. STATS RIBBON */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center border-0 shadow-sm bg-light">
            <Card.Body>
              <h6 className="text-muted mb-1">Total Charges</h6>
              <h4 className="mb-0 text-dark">₹ {summary.totalCredit.toLocaleString()}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center border-0 shadow-sm bg-light">
            <Card.Body>
              <h6 className="text-muted mb-1">Total Paid</h6>
              <h4 className="mb-0 text-success">₹ {summary.totalDebit.toLocaleString()}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className={`text-center border-0 shadow-sm ${summary.balance > 0 ? 'bg-danger text-white' : 'bg-success text-white'}`}>
            <Card.Body>
              <h6 className="mb-1">{summary.balance > 0 ? "Pending Balance" : "Refund Due / Clear"}</h6>
              <h4 className="mb-0">₹ {Math.abs(summary.balance).toLocaleString()}</h4>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 2. TRANSACTION LEDGER TABLE */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Transaction History</h5>
          <Button variant="outline-secondary" size="sm" onClick={() => window.print()}>
            🖨️ Print Bill
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Description</th>
                <th>Method</th>
                <th className="text-end">Charge (+)</th>
                <th className="text-end">Payment (-)</th>
              </tr>
            </thead>
            <tbody>
              {summary.transactions.map((txn) => (
                <tr key={txn._id}>
                  <td>{new Date(txn.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Badge bg="light" text="dark" className="border text-uppercase">
                      {txn.category?.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td>
                    <div className="small fw-bold">{txn.description}</div>
                    {txn.referenceId && <small className="text-muted">Ref: {txn.referenceId}</small>}
                  </td>
                  <td>
                    <small className="text-uppercase">{txn.paymentMode || '—'}</small>
                  </td>
                  <td className="text-end text-danger">
                    {txn.type === "credit" ? `₹ ${txn.amount.toLocaleString()}` : "—"}
                  </td>
                  <td className="text-end text-success">
                    {txn.type === "debit" ? `₹ ${txn.amount.toLocaleString()}` : "—"}
                  </td>
                </tr>
              ))}
              {summary.transactions.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">No entries recorded yet.</td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-light fw-bold">
              <tr>
                <td colSpan="4" className="text-end">Final Balance</td>
                <td colSpan="2" className={`text-end ${summary.balance > 0 ? 'text-danger' : 'text-success'}`}>
                   ₹ {summary.balance.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TransactionSummary;